import React, { memo } from "react";
import { createPortal } from "react-dom";
import { useApp } from "@/contexts/AppContext";
import RiskDialGauge from "./RiskDialGauge";
import HierarchyTree from "./HierarchyTree";

export default memo(function OverviewTab() {
  const { categoryDist, ventureDist } = useApp();

  // Deduplicate and aggregate results by name
  const normalize = (data, key) => {
    const map = {};
    data.forEach(item => {
      const name = (item[key] || "Unknown").trim();
      if (!map[name]) {
        map[name] = { ...item, [key]: name };
      } else {
        map[name].signals = (map[name].signals || 0) + (item.signals || 0);
        map[name].critical = (map[name].critical || 0) + (item.critical || 0);
        const totalSignals = map[name].signals;
        if (totalSignals > 0) {
          map[name].avg_risk = ((map[name].avg_risk || 0) * (totalSignals - (item.signals || 0)) + (item.avg_risk || 0) * (item.signals || 0)) / totalSignals;
        }
      }
    });
    return Object.values(map);
  };


  const allCategories = normalize(categoryDist, "category").sort((a, b) => (b.critical || 0) - (a.critical || 0));
  const allVentures = normalize(ventureDist, "venture").sort((a, b) => (b.critical || 0) - (a.critical || 0));

  return (
    <div className="h-[calc(100vh-110px)] flex flex-col overflow-hidden" data-testid="overview-tab">
      <div className="grid grid-cols-1 xl:grid-cols-[60%_40%] gap-4 items-start flex-1 min-h-0 pb-28 overflow-y-auto custom-scrollbar pr-1">
        {/* LEFT COLUMN: DIALS — 60% width */}
        <div
          className="flex flex-col gap-4 min-h-0"
          style={{ contain: 'layout style', transition: 'none' }}
        >
          <section className="panel p-3 flex flex-col min-h-[260px]">
            <div className="flex items-center justify-between mb-2 flex-shrink-0 border-b border-hair pb-2">
              <div>
                <div className="label-micro text-primary/60 uppercase tracking-widest">Risk Dial · All Ventures</div>
                <h3 className="font-display text-lg tracking-tight">Business Risk Landscape</h3>
              </div>
              <div className="label-micro font-medium">{allVentures.length} ventures</div>
            </div>
            <div className="flex items-start gap-2 overflow-x-auto flex-1 custom-scrollbar pb-2 pr-2 min-h-[166px]">
              {allVentures.length > 0 ? allVentures.map((v) => (
                <div key={v.venture || v.name} className="bg-[var(--bg-surface)] p-0.5 min-w-[280px] flex-shrink-0">
                  <RiskDialGauge item={v} type="venture" />
                </div>
              )) : (
                <div className="flex-1 flex gap-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="min-w-[280px] h-[166px] bg-black/[0.03] animate-pulse rounded-sm border border-hair" />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="panel p-3 flex flex-col min-h-[260px]">
            <div className="flex items-center justify-between mb-2 flex-shrink-0 border-b border-hair pb-2">
              <div>
                <div className="label-micro text-primary/60 uppercase tracking-widest">Risk Dial · All Categories</div>
                <h3 className="font-display text-lg tracking-tight">Category Risk Landscape</h3>
              </div>
              <div className="flex items-center gap-4">
                <div className="label-micro font-medium">{allCategories.length} categories</div>
              </div>
            </div>
            <div className="flex items-start gap-2 overflow-x-auto flex-1 custom-scrollbar pb-2 pr-2 min-h-[166px]">
              {allCategories.length > 0 ? allCategories.map((c) => (
                <div key={c.category} className="bg-[var(--bg-surface)] p-0.5 min-w-[280px] flex-shrink-0">
                  <RiskDialGauge item={c} type="category" />
                </div>
              )) : (
                <div className="flex-1 flex gap-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="min-w-[280px] h-[166px] bg-black/[0.03] animate-pulse rounded-sm border border-hair" />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN: HIERARCHY — 40% width, always visible */}
        <section
          className="panel p-3 flex flex-col"
          style={{
            contain: 'layout style'
          }}
        >
          <div className="flex items-center justify-between mb-3 flex-shrink-0 border-b border-hair pb-2">
            <div>
              <div className="label-micro uppercase tracking-widest">Hierarchical Drilldown</div>
              <h3 className="font-display text-lg">Business → Site → Category</h3>
            </div>
          </div>
          <div className="custom-scrollbar pr-1">
            <HierarchyTree />
          </div>
        </section>
      </div>
    </div>
  );
});
